import { useMemo, useState } from 'react';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import {
  Clock,
  MoneyWavy,
  PenNib,
  Receipt,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { formatCurrency } from '../../../../../utils/CurrencyUtils';

import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { formatDate } from '../../../../../utils/DateUtils';
import { formatDeadline } from './hooks/cashAdvanceOptionsHelpers';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FetchLoansOrderBy,
  FetchLoansRequestBody,
  LoanType,
  LoanStatuses,
  LoanWithReason,
  LoanRequestWithTerms,
  CustomerWithDriverOnboarding,
} from '../../../../../types/apiTypes';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { ClipLoader } from 'react-spinners';
import { Customer } from '../../../../../types/dbTypes';
import { isPreActiveCustomer, getDemoLoans } from '../home/utils/demoData';
import { DemoDataAlert } from '../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../home/components/DemoDataBadge';

interface LoansProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  addClass?: string;
  selectedMenuTab: string;
  setSelectedMenuTab: (tab: string) => void;
  setSelectedLoan: (loan: any) => void;
}
function Loans({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  addClass,
  selectedMenuTab,
  setSelectedMenuTab,
  setSelectedLoan,
}: LoansProps) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/demo')
    ? '/demo'
    : '/dashboard';
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const [loanTypeFilter] = useState<LoanType>(LoanType.CASH_ADVANCE);

  // ============================================================================
  // Pre-Active Customer Detection
  // ============================================================================

  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;

  // Fetch loan requests awaiting consent signature
  const { data: pendingConsentRequests = [] } = useQuery<
    LoanRequestWithTerms[]
  >({
    queryKey: ['pendingConsentLoanRequests', userId],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/credit/${userId}/pending-consent`,
        'get',
        {}
      );
      return response?.data?.data?.loanRequests || [];
    },
    enabled: shouldFetchRealData,
  });

  const { data: loans = [], isLoading } = useQuery<LoanWithReason[]>({
    queryKey: ['loans', userId ?? '', selectedMenuTab, loanTypeFilter],
    queryFn: async () => {
      // Map 'paid' UI tab value back to the API status value 'closed'
      const apiStatus =
        selectedMenuTab === 'paid' ? LoanStatuses.Closed : selectedMenuTab;
      const response = await callProtectedApi(`/credit/loans`, 'post', {
        customer_id: userId,
        status: apiStatus,
        order_by: FetchLoansOrderBy.ORIGINATED_AT,
        order_direction: 'DESC',
        type: loanTypeFilter,
      } as FetchLoansRequestBody);
      const loans = response?.data?.data?.loans || [];
      loans.map((loan: any) => {
        loan.paid = loan?.total_cost_of_loan - loan?.current_balance;
        return loan;
      });
      return loans || [];
    },
    enabled: shouldFetchRealData,
  });

  // Split pending loan requests into two stages:
  // (1) active options round — customer needs to pick/decline
  // (2) selected, awaiting consent signature — existing flow
  const { pendingOptionsRequests, pendingSignatureRequests } = useMemo(() => {
    const optionsRound: LoanRequestWithTerms[] = [];
    const signatureRound: LoanRequestWithTerms[] = [];
    for (const req of pendingConsentRequests) {
      // The backend already excludes expired/terminal rounds from this list, so
      // we classify on authoritative timestamp facts rather than re-checking the
      // deadline against the device clock (which could drift or go stale and
      // mis-bucket a request). A request is an active options round iff it was
      // presented and has no terminal stamp yet; everything else — including
      // already-selected rows awaiting signature — is the signature stage.
      const isActiveOptions =
        !!req.options_presented_at &&
        !req.options_selected_at &&
        !req.options_declined_at &&
        !req.options_expired_at;
      if (isActiveOptions) {
        optionsRound.push(req);
      } else {
        signatureRound.push(req);
      }
    }
    return {
      pendingOptionsRequests: optionsRound,
      pendingSignatureRequests: signatureRound,
    };
  }, [pendingConsentRequests]);

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayLoans = useMemo(() => {
    if (isPreActive) {
      return getDemoLoans().filter((loan) => loan.type === loanTypeFilter);
    }
    return loans;
  }, [isPreActive, loans, loanTypeFilter]);

  const filteredLoans = useMemo(() => {
    switch (selectedMenuTab) {
      case LoanStatuses.Active:
        return displayLoans?.filter(
          (loan) =>
            loan?.status === LoanStatuses.Active &&
            loan?.current_balance &&
            loan.current_balance > 0
        );
      case LoanStatuses.Requested:
        return displayLoans?.filter(
          (loan) => loan?.status === LoanStatuses.Requested
        );
      case 'paid':
        return displayLoans?.filter((loan) => loan?.current_balance === 0);
      default:
        return [];
    }
  }, [selectedMenuTab, displayLoans]);

  // Loading states: Never show loading for demo data
  const displayLoading = isPreActive ? false : isLoading;

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full flex-col space-y-[16px] overflow-x-hidden overflow-y-hidden pt-[16px] sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px]">
            {isPreActive && <DemoDataAlert />}
            {/* Review Options Banner */}
            {pendingOptionsRequests.length > 0 && (
              <div className="flex w-full items-center justify-between rounded-lg border border-accent bg-bgyellow px-5 py-4">
                <div className="flex items-center gap-3">
                  <Clock size={24} weight="fill" className="text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-primary">
                      Review repayment options
                      {pendingOptionsRequests[0]?.options_expire_at
                        ? ` — expires ${formatDeadline(
                            pendingOptionsRequests[0].options_expire_at
                          )}`
                        : ''}
                    </p>
                    <p className="text-sm text-graysecondary">
                      Choose your preferred plan to continue.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `${basePath}/banking/cash-advance/options/${pendingOptionsRequests[0]?.request_id}`
                    )
                  }
                  className="whitespace-nowrap rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-80"
                >
                  Review Options
                </button>
              </div>
            )}
            {/* Consent Banner */}
            {pendingSignatureRequests.length > 0 && (
              <div className="flex w-full items-center justify-between rounded-lg border border-green-300 bg-green-50 px-5 py-4">
                <div className="flex items-center gap-3">
                  <PenNib size={24} weight="fill" className="text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-primary">
                      Your Cash Advance has been approved!
                    </p>
                    <p className="text-sm text-graysecondary">
                      Sign the agreement to receive your funds.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `${basePath}/banking/cash-advance/consent/${pendingSignatureRequests[0]?.request_id}`
                    )
                  }
                  className="whitespace-nowrap rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-80"
                >
                  Sign Agreement
                </button>
              </div>
            )}
            {/* Loan Type Segmented Control — Fuel Advances disabled */}
            {/* <div className="inline-flex items-center self-start rounded-lg bg-[#F0F1F3] p-1">
              <button
                type="button"
                className={`rounded-md px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                  loanTypeFilter === LoanType.CASH_ADVANCE
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-graysecondary hover:text-primary'
                }`}
                onClick={() => {
                  setLoanTypeFilter(LoanType.CASH_ADVANCE);
                  navigate(
                    `${basePath}/banking/cash-advances${selectedMenuTab === 'paid' ? '/paid' : selectedMenuTab === LoanStatuses.Requested ? '/requested' : '/active'}`,
                    {
                      replace: true,
                      state: { loanType: LoanType.CASH_ADVANCE },
                    }
                  );
                }}
              >
                Cash Advances
              </button>
              <button
                type="button"
                className={`rounded-md px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                  loanTypeFilter === LoanType.FUEL_ADVANCE
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-graysecondary hover:text-primary'
                }`}
                onClick={() => {
                  setLoanTypeFilter(LoanType.FUEL_ADVANCE);
                  navigate(
                    `${basePath}/banking/cash-advances${selectedMenuTab === 'paid' ? '/paid' : selectedMenuTab === LoanStatuses.Requested ? '/requested' : '/active'}`,
                    {
                      replace: true,
                      state: { loanType: LoanType.FUEL_ADVANCE },
                    }
                  );
                }}
              >
                Fuel Advances
              </button>
            </div> */}
            {/* Status Tabs & CTA */}
            <div className="flex w-full flex-col items-start justify-between space-y-4 lg:flex-row lg:items-center">
              <nav className="flex items-center border-b border-bordergray">
                {[
                  {
                    label: 'Active',
                    value: LoanStatuses.Active,
                    path: '/active',
                  },
                  {
                    label: 'Requested',
                    value: LoanStatuses.Requested,
                    path: '/requested',
                  },
                  { label: 'Paid', value: 'paid', path: '/paid' },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    disabled={isPreActive}
                    onClick={() => {
                      setSelectedMenuTab(tab.value);
                      navigate(`${basePath}/banking/cash-advances${tab.path}`, {
                        state: { loanType: loanTypeFilter },
                      });
                    }}
                    className={`relative px-5 pb-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      selectedMenuTab === tab.value
                        ? 'text-primary'
                        : 'text-graysecondary hover:text-primary'
                    }`}
                  >
                    {tab.label}
                    {selectedMenuTab === tab.value && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-accent" />
                    )}
                  </button>
                ))}
              </nav>
              <AccentButtonOutline
                onClick={() =>
                  navigate(`${basePath}/banking/cash-advance`, {
                    state: { advanceType: LoanType.CASH_ADVANCE },
                  })
                }
                disabled={
                  !(userRecord as Customer)?.is_cash_advance_active ||
                  isPreActive
                }
                addClass="rounded-full w-[240px] h-[48px] flex flex-row items-center align-middle space-x-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MoneyWavy size={20} />
                <p className="text-[16px]">Request Cash Advance</p>
              </AccentButtonOutline>
            </div>
            <DemoDataWrapper showBadge={isPreActive}>
              <div
                className={`flex h-[calc(100vh-200px)] w-full flex-col items-center gap-x-[16px] rounded-[4px] border-[1px] align-middle ${addClass} bg-white`}
              >
                {filteredLoans && filteredLoans?.length > 0 ? (
                  <div className="w-full overflow-y-auto overflow-x-hidden bg-white pb-16">
                    <table className="min-w-full table-auto bg-white">
                      <thead className="bg-primarybg">
                        <tr>
                          <th className="hidden px-4 py-2 text-left text-[14px] font-[600] text-primary md:block">
                            Purpose
                          </th>
                          {selectedMenuTab === LoanStatuses.Requested ? (
                            <>
                              <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                                Requested Date
                              </th>
                              <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                                Amount
                              </th>
                              <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                                Decision
                              </th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                                Issuance Date
                              </th>
                              <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                                Paid
                              </th>
                              <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                                Left
                              </th>
                              <th className="hidden px-4 py-2 text-left text-[14px] font-[600] text-primary lg:inline-block">
                                Progress
                              </th>
                              <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                                Due Date
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="border-bordergray bg-white">
                        {filteredLoans.map((loan: LoanWithReason, index) => (
                          <tr
                            onClick={() => {
                              if (
                                selectedMenuTab === LoanStatuses.Requested ||
                                isPreActive
                              ) {
                                return;
                              }
                              setSelectedLoan(loan);
                              navigate(
                                `${basePath}/banking/cash-advances/details/${loan?.loan_id}`,
                                { state: { loanType: loanTypeFilter } }
                              );
                            }}
                            key={index}
                            className={`border-b-[1px] border-bordergray ${selectedMenuTab !== 'requested' ? 'hover:cursor-pointer hover:bg-ctaactivegreen' : ''}`}
                          >
                            <td className="line-clamp-1 hidden max-w-[180px] overflow-hidden text-ellipsis text-nowrap px-4 py-2 text-[14px] text-primary md:block">
                              {loan?.loanRequest?.reason}
                            </td>
                            {selectedMenuTab === LoanStatuses.Requested ? (
                              <>
                                <td className="px-4 py-2 text-[14px] text-primary">
                                  {formatDate(
                                    loan?.originated_at
                                      ? new Date(loan?.originated_at)
                                      : new Date(),
                                    true
                                  )}
                                </td>
                                <td className="px-4 py-2 text-[14px] text-primary">
                                  {formatCurrency(
                                    loan?.total_cost_of_loan ?? 0
                                  )}
                                </td>
                                <td className="px-4 py-2 text-[14px] text-primary">
                                  {loan?.decision
                                    ? loan?.decision[0].toUpperCase() +
                                      loan?.decision.slice(1)
                                    : 'None'}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-2 text-[14px] text-primary">
                                  {formatDate(
                                    loan?.originated_at
                                      ? new Date(loan?.originated_at)
                                      : new Date(),
                                    true
                                  )}
                                </td>
                                {loan?.total_cost_of_loan != null &&
                                  loan?.current_balance != null && (
                                    <td className="px-4 py-2 text-right text-[14px] text-primary">
                                      {formatCurrency(
                                        Number(loan?.total_cost_of_loan) -
                                          Number(loan?.current_balance)
                                      )}
                                    </td>
                                  )}
                                {loan?.current_balance != null && (
                                  <td className="px-4 py-2 text-right text-[14px] text-primary">
                                    {formatCurrency(loan?.current_balance)}
                                  </td>
                                )}
                                {loan?.current_balance != null &&
                                  loan?.total_cost_of_loan != null && (
                                    <td className="hidden h-10 w-[250px] max-w-[250px] px-4 py-2 text-right align-middle text-[14px] text-primary lg:table-cell">
                                      <div className="flex h-full w-full flex-row items-center justify-center">
                                        <div className="flex h-[12px] w-full flex-row overflow-hidden rounded-[10px] bg-[#E6E6E6]">
                                          {Number((loan as any)?.paid ?? 0) >
                                            0 &&
                                            Number(
                                              loan?.total_cost_of_loan ?? 0
                                            ) > 0 && (
                                              <div
                                                className={`h-full ${
                                                  Number(
                                                    loan?.current_balance ?? 0
                                                  ) === 0
                                                    ? 'bg-[#6A9E68]'
                                                    : isPastDate(
                                                          new Date(
                                                            loan?.end_date ?? ''
                                                          )
                                                        )
                                                      ? 'bg-[#C64949]'
                                                      : isWithinSevenDays(
                                                            new Date(
                                                              loan?.end_date ??
                                                                ''
                                                            )
                                                          )
                                                        ? 'bg-[#E2660C]'
                                                        : 'bg-[#1D213B]'
                                                }`}
                                                style={{
                                                  width: `${Math.min(100, Math.max(0, (Number((loan as any)?.paid ?? 0) / Number(loan?.total_cost_of_loan ?? 1)) * 100))}%`,
                                                }}
                                              />
                                            )}
                                        </div>
                                      </div>
                                    </td>
                                  )}
                                {loan?.current_balance != null &&
                                  loan?.end_date && (
                                    <td className="px-4 py-2 text-right text-[14px] text-primary">
                                      <div
                                        className={`flex items-center justify-end ${isPastDate(new Date(loan?.end_date)) && loan?.current_balance > 0 ? 'font-semibold text-[#C64949]' : ''} ${!isPastDate(new Date(loan?.end_date)) && loan?.current_balance > 0 && isWithinSevenDays(new Date(loan?.end_date)) ? 'font-semibold text-[#E2660C]' : ''}`}
                                      >
                                        {!isPastDate(
                                          new Date(loan?.end_date)
                                        ) &&
                                          (loan?.current_balance ?? 0) > 0 &&
                                          isWithinSevenDays(
                                            new Date(loan?.end_date)
                                          ) && (
                                            <Warning
                                              className="mr-2 text-[#E2660C]"
                                              size={20}
                                            />
                                          )}
                                        {isPastDate(new Date(loan?.end_date)) &&
                                          loan?.current_balance > 0 && (
                                            <XCircle
                                              className="mr-2 text-[#C64949]"
                                              size={20}
                                            />
                                          )}
                                        {formatDate(
                                          new Date(loan?.end_date),
                                          true
                                        )}
                                      </div>
                                    </td>
                                  )}
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex h-[calc(100vh-200px)] w-full flex-col items-center justify-center space-y-2">
                    <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                      <Receipt size={40} className="text-graysecondary" />
                    </div>
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Cash Advances Found
                    </p>
                  </div>
                )}
              </div>
            </DemoDataWrapper>
          </div>
        );
    }
  };

  if (displayLoading) {
    return (
      <ClipLoader
        color="#1D213B"
        loading={true}
        size={60}
        aria-label="Loading Spinner"
        data-testid="loader"
        className="mt-10 flex h-full w-full justify-center"
      />
    );
  }

  return <>{renderScreenView(screenView)}</>;
}

export default Loans;

export const isWithinSevenDays = (dueDateString: string | Date): boolean => {
  const currentDate = new Date(); // Get the current date
  const dueDate = new Date(dueDateString); // Convert the dueDate to a Date object

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = dueDate.getTime() - currentDate.getTime();

  // Convert milliseconds to days
  const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

  // Check if the due date is within 7 days in the future
  return differenceInDays >= 0 && differenceInDays <= 7;
};

export const isPastDate = (date: string | Date): boolean => {
  const currentDate = new Date(); // Get the current date and time
  const targetDate = new Date(date); // Convert the input to a Date object

  // Return true if the current date is after the target date
  return currentDate.getTime() > targetDate.getTime();
};
